import { ERROR_MESSAGES } from '../currency-helpers.constants';
import type { CurrencyInput, CurrencyValidation } from '../currency-helpers.type';

/**
 * Safely parses various currency input types into a number or null
 *
 * Handles:
 * - Numbers: Pass through as-is
 * - Strings: Parse numeric strings, handling currency symbols, commas, spaces
 * - Null/Undefined: Return null
 * - Invalid values: Return null
 *
 * @param {CurrencyInput} input - The input to parse
 * @returns {number | null} Parsed numeric value or null if invalid
 */
const safeParseCurrencyInput = (input: CurrencyInput): number | null => {
  // Handle null/undefined
  if (input === null || input === undefined) {
    return null;
  }

  // Handle numbers
  if (typeof input === 'number') {
    // Check for NaN and Infinity
    if (!Number.isFinite(input)) {
      return null;
    }
    return input;
  }

  // Handle strings
  if (typeof input === 'string') {
    // Remove whitespace
    const trimmed = input.trim();

    // Empty string
    if (trimmed === '') {
      return null;
    }

    // Remove currency symbols, commas, spaces (including HAIR SPACE U+200A)
    // This regex removes: $, €, £, commas, regular spaces, hair spaces, and other common formatting
    const cleaned = trimmed
      .replace(/[$€£¥₹]/g, '') // Currency symbols
      .replace(/,/g, '') // Thousands separators
      .replace(/[\s\u200A]/g, ''); // Spaces including HAIR SPACE

    // Parse the cleaned string
    const parsed = Number.parseFloat(cleaned);

    // Check if parsing was successful
    if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
      return null;
    }

    return parsed;
  }

  // Unsupported type
  return null;
};

/**
 * Validates a currency input and returns detailed validation results
 *
 * @param {CurrencyInput} input - The input to validate
 * @returns {CurrencyValidation} Validation result with parsed value
 */
const validateCurrencyInput = (input: CurrencyInput): CurrencyValidation => {
  try {
    const parsedValue = safeParseCurrencyInput(input);

    if (parsedValue === null) {
      return {
        isValid: false,
        error: ERROR_MESSAGES.INVALID_INPUT,
        parsedValue: null,
      };
    }

    return {
      isValid: true,
      parsedValue,
    };
  } catch (_) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.PARSING_ERROR,
      parsedValue: null,
    };
  }
};

/**
 * Quick validation check for currency input
 *
 * @param {CurrencyInput} input - The input to validate
 * @returns {boolean} True if valid, false otherwise
 */
const isValidCurrency = (input: CurrencyInput): boolean => {
  const result = validateCurrencyInput(input);
  return result.isValid;
};

export { isValidCurrency, safeParseCurrencyInput, validateCurrencyInput };
